<!--
  @component FileBrowser — folder-tree + file-list workbench.
  tier:      primitive (visual atom + light interaction chrome)
  purpose:   shared shell for any "browse files in a substrate"
             surface. ProjectStorage uses it for the project's
             bucket; future admin Storage panel reuses it for
             cross-tenant introspection; a Snapshots view reuses
             it for restore-point file diffs.

  CONTRACT
  ─────────────────────────────────────────────────────────────────
  Parent supplies:
    entries:   readonly FileEntry[]    (flat list with `path` keys)
    currentPath: "/" or "/foo/bar"     (the folder being viewed)
    onNavigate(path): void             (folder click)
    onFileClick(file): void            (file click — usually opens preview)
    onAction(action, file): void       (per-file actions: share, move, trash, …)
    actions?:  readonly FileAction[]   (which actions to show in the per-row menu)
    pending?:  boolean                 (disable everything while parent works)

  Parent owns:
    - the entry list (flat or paginated; FileBrowser doesn't fetch)
    - the current path (so the URL or the parent's state machine
      can drive navigation; FileBrowser is read-mostly)
    - what an "action" does (FileBrowser just emits the click)

  FileBrowser owns:
    - splitting entries into "folders" vs "files" for the current path
    - the breadcrumb chain (parsed from currentPath)
    - the per-row action menu chrome
    - keyboard activation (Enter / Space on folders/files)
-->
<script lang="ts" module>
  /** A single entry in the browser. Flat — folders are entries
   *  with `isFolder: true`. Paths are POSIX-style with leading "/". */
  export interface FileEntry {
    readonly path: string;            // "/photos/2024/cat.png"
    readonly name: string;            // "cat.png"
    readonly isFolder: boolean;
    readonly sizeBytes?: number;      // omit for folders
    readonly modifiedAtMs?: number;
    readonly mime?: string;           // "image/png"
  }

  /** Per-row action descriptor. */
  export interface FileAction {
    readonly id: string;              // "share", "move", "trash"
    readonly label: string;           // "Share", "Move", "Trash"
    readonly destructive?: boolean;
  }
</script>

<script lang="ts">
  interface Props {
    entries: readonly FileEntry[];
    /** Active folder path. "/" = root. */
    currentPath: string;
    onNavigate: (path: string) => void;
    /** File click — typically opens a preview side-panel. Folder
     *  clicks always navigate (not file click). */
    onFileClick?: (file: FileEntry) => void;
    /** Per-row action click. */
    onAction?: (actionId: string, file: FileEntry) => void;
    actions?: readonly FileAction[];
    pending?: boolean;
    emptyLabel?: string;
  }
  const {
    entries,
    currentPath,
    onNavigate,
    onFileClick,
    onAction,
    actions = [
      { id: "share", label: "Share" },
      { id: "move",  label: "Move" },
      { id: "trash", label: "Trash", destructive: true },
    ],
    pending = false,
    emptyLabel = "empty folder",
  }: Props = $props();

  // ── Breadcrumbs ────────────────────────────────────────────────
  const crumbs = $derived.by(() => {
    if (currentPath === "/" || currentPath === "") return [{ name: "root", path: "/" }];
    const parts = currentPath.split("/").filter(Boolean);
    const out: { name: string; path: string }[] = [{ name: "root", path: "/" }];
    let acc = "";
    for (const p of parts) {
      acc += "/" + p;
      out.push({ name: p, path: acc });
    }
    return out;
  });

  // ── Split entries: only ones in currentPath ────────────────────
  function parentOf(p: string): string {
    if (p === "/" || p === "") return "/";
    const trimmed = p.replace(/\/$/, "");
    const lastSlash = trimmed.lastIndexOf("/");
    if (lastSlash <= 0) return "/";
    return trimmed.slice(0, lastSlash);
  }

  const inCurrentFolder = $derived(
    entries.filter((e) => parentOf(e.path) === currentPath),
  );

  // Folders first, then files. Each sorted alphabetically.
  const folders = $derived(
    [...inCurrentFolder.filter((e) => e.isFolder)].sort(
      (a, b) => a.name.localeCompare(b.name),
    ),
  );
  const files = $derived(
    [...inCurrentFolder.filter((e) => !e.isFolder)].sort(
      (a, b) => a.name.localeCompare(b.name),
    ),
  );

  // ── Open action menu state ────────────────────────────────────
  let openMenuPath = $state<string | null>(null);
  function toggleMenu(path: string): void {
    openMenuPath = openMenuPath === path ? null : path;
  }
  function fireAction(actionId: string, entry: FileEntry): void {
    openMenuPath = null;
    onAction?.(actionId, entry);
  }

  // ── Formatting ───────────────────────────────────────────────
  function fmtSize(b?: number): string {
    if (b === undefined) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }
  function timeAgo(ms?: number): string {
    if (!ms) return "—";
    const diff = Date.now() - ms;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  }
  function iconFor(entry: FileEntry): string {
    if (entry.isFolder) return "📁";
    const mime = entry.mime ?? "";
    if (mime.startsWith("image/")) return "🖼";
    if (mime.startsWith("video/")) return "🎞";
    if (mime.startsWith("audio/")) return "🎵";
    if (mime.startsWith("text/") || mime === "application/json") return "📄";
    return "📦";
  }
</script>

<div class="browser" class:pending>
  <!-- ── Breadcrumbs ─────────────────────────────────────────── -->
  <nav class="crumbs" aria-label="Folder breadcrumb">
    {#each crumbs as c, i (c.path)}
      <button
        type="button"
        class="crumb"
        class:current={i === crumbs.length - 1}
        onclick={() => onNavigate(c.path)}
        disabled={pending}
      >
        {c.name}
      </button>
      {#if i < crumbs.length - 1}
        <span class="sep" aria-hidden="true">/</span>
      {/if}
    {/each}
  </nav>

  <!-- ── Listing ─────────────────────────────────────────────── -->
  <div class="listing" role="list">
    {#if folders.length === 0 && files.length === 0}
      <p class="empty">{emptyLabel}</p>
    {:else}
      <!-- Folders -->
      {#each folders as f (f.path)}
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <!-- svelte-ignore a11y_no_noninteractive_tabindex
             — folders are click-to-navigate; role+tabindex+kbd
             handler IS the intended pattern. -->
        <div
          class="row folder"
          role="listitem"
          tabindex="0"
          onclick={() => onNavigate(f.path)}
          onkeydown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onNavigate(f.path); } }}
        >
          <span class="icon" aria-hidden="true">{iconFor(f)}</span>
          <span class="name">{f.name}</span>
          <span class="size">—</span>
          <span class="time">{timeAgo(f.modifiedAtMs)}</span>
          <span class="actions-cell"></span>
        </div>
      {/each}
      <!-- Files -->
      {#each files as f (f.path)}
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <div
          class="row file"
          role="listitem"
          tabindex="0"
          onclick={() => onFileClick?.(f)}
          onkeydown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFileClick?.(f); } }}
        >
          <span class="icon" aria-hidden="true">{iconFor(f)}</span>
          <span class="name">{f.name}</span>
          <span class="size">{fmtSize(f.sizeBytes)}</span>
          <span class="time">{timeAgo(f.modifiedAtMs)}</span>
          {#if actions.length > 0 && onAction}
            <span class="actions-cell">
              <button
                type="button"
                class="action-btn"
                aria-haspopup="menu"
                aria-expanded={openMenuPath === f.path}
                onclick={(e) => { e.stopPropagation(); toggleMenu(f.path); }}
                disabled={pending}
              >
                ⋯
              </button>
              {#if openMenuPath === f.path}
                <div class="action-menu" role="menu">
                  {#each actions as a (a.id)}
                    <button
                      type="button"
                      class="menu-item"
                      class:destructive={a.destructive}
                      onclick={(e) => { e.stopPropagation(); fireAction(a.id, f); }}
                      role="menuitem"
                    >
                      {a.label}
                    </button>
                  {/each}
                </div>
              {/if}
            </span>
          {:else}
            <span class="actions-cell"></span>
          {/if}
        </div>
      {/each}
    {/if}
  </div>
</div>

<style>
  .browser {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    font-family: var(--type-font);
  }
  .browser.pending { opacity: 0.6; }

  /* Crumbs */
  .crumbs {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-1, 4px);
    padding: var(--space-2) var(--space-3);
    background: var(--surface-sunken);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-sm);
    font-size: 12px;
  }
  .crumb {
    appearance: none;
    background: transparent;
    border: 0;
    color: var(--accent-text);
    font: inherit;
    font-size: 12px;
    padding: var(--space-1, 4px) var(--space-2);
    border-radius: var(--radius-xs);
    cursor: pointer;
  }
  .crumb:hover:not(:disabled) { background: var(--bg-hover); }
  .crumb.current {
    color: var(--text-primary);
    cursor: default;
  }
  .crumb.current:hover { background: transparent; }
  .crumb:disabled { cursor: not-allowed; }
  .sep { color: var(--text-tertiary); font-size: 11px; }

  /* Listing */
  .listing {
    display: flex;
    flex-direction: column;
    background: var(--surface-sunken);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-md);
    overflow: auto;
    max-height: 56vh;
  }
  .empty {
    margin: 0;
    padding: var(--space-5) var(--space-4);
    font-size: 12px;
    color: var(--text-tertiary);
    text-align: center;
    font-style: italic;
  }

  /* Row */
  .row {
    display: grid;
    grid-template-columns: 28px 1fr 120px 120px 40px;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    border-bottom: 0.5px solid var(--border-subtle);
    cursor: pointer;
    transition: background var(--ease-fast);
    font-size: 12px;
  }
  .row:hover { background: var(--bg-hover); }
  .row:focus-visible {
    outline: none;
    background: var(--bg-hover);
    box-shadow: inset 0 0 0 1px var(--border-focus);
  }
  .row.folder .name { color: var(--accent-text); font-weight: 500; }
  .icon { font-size: 14px; line-height: 1; }
  .name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--text-primary);
  }
  .size, .time {
    font-family: var(--type-mono);
    font-size: 11px;
    color: var(--text-tertiary);
    text-align: right;
  }

  /* Action button + menu */
  .actions-cell { position: relative; display: flex; justify-content: flex-end; }
  .action-btn {
    appearance: none;
    background: transparent;
    border: 0;
    color: var(--text-tertiary);
    font: inherit;
    font-size: 14px;
    padding: 0 var(--space-2);
    cursor: pointer;
    border-radius: var(--radius-xs);
  }
  .action-btn:hover:not(:disabled) {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
  .action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .action-menu {
    position: absolute;
    top: calc(100% + 2px);
    right: 0;
    min-width: 140px;
    background: var(--surface-raised);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-sm);
    box-shadow: var(--shadow-md);
    padding: var(--space-1, 4px);
    z-index: 50;
    display: flex;
    flex-direction: column;
  }
  .menu-item {
    appearance: none;
    background: transparent;
    border: 0;
    color: var(--text-primary);
    text-align: left;
    padding: var(--space-2) var(--space-3);
    font: inherit;
    font-size: 12px;
    cursor: pointer;
    border-radius: var(--radius-xs);
  }
  .menu-item:hover { background: var(--bg-hover); }
  .menu-item.destructive { color: var(--error); }
</style>
